/**
 * ActiveFunctionsPanel.tsx
 *
 * Displays aggregated view of currently running Azure Functions.
 * Shows total count, breakdown by message type and entity type,
 * plus the longest-running function for debugging stuck processes.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, Clock, AlertTriangle } from "lucide-react";
import { cn } from "../lib/utils";
import type { QueueWorkerInfo } from "../lib/api";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";

interface ActiveFunctionsPanelProps {
  activeWorkers: QueueWorkerInfo[];
  isLoading?: boolean;
}

interface MessageTypeGroup {
  type: string;
  displayName: string;
  count: number;
  entities: string[];
  color: string;
}

interface EntityTypeGroup {
  entity: string;
  count: number;
}

/**
 * Map message types to display names and colors
 */
const MESSAGE_TYPE_CONFIG: Record<string, { displayName: string; color: string }> = {
  // Backend MappingTaskType enum values
  'SqlSyncBatch': { displayName: 'SQL Sync', color: 'bg-blue-500' },
  'SqlSyncChangeDetection': { displayName: 'Change Detection', color: 'bg-purple-500' },
  'SqlSyncChunk': { displayName: 'SqlSyncChunk', color: 'bg-cyan-500' },
  'FieldSyncChunk': { displayName: 'FieldSyncChunk', color: 'bg-teal-500' },
  'EntityMapping': { displayName: 'Entity Mapping', color: 'bg-green-500' },
  'FieldMapping': { displayName: 'Field Mapping', color: 'bg-orange-500' },
  // Legacy dot-notation values (for backwards compatibility)
  'Sql.EntitySync': { displayName: 'SQL Sync', color: 'bg-blue-500' },
  'Sql.ChangeDetection': { displayName: 'Change Detection', color: 'bg-purple-500' },
  'Sql.Chunk': { displayName: 'Chunk Processing', color: 'bg-cyan-500' },
};

function getMessageTypeConfig(messageType: string): { displayName: string; color: string } {
  return MESSAGE_TYPE_CONFIG[messageType] || {
    displayName: messageType.split('.').pop() || messageType,
    color: 'bg-gray-500'
  };
}

/**
 * Progress bar for message type breakdown
 */
function MessageTypeBar({ group, total }: { group: MessageTypeGroup; total: number }) {
  const percentage = total > 0 ? (group.count / total) * 100 : 0;
  const uniqueEntities = Array.from(new Set(group.entities));

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <div className={cn("w-3 h-3 rounded-sm", group.color)} />
          <span className="font-medium">{group.displayName}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-xs">
            {uniqueEntities.slice(0, 3).join(', ')}
            {uniqueEntities.length > 3 && ` +${uniqueEntities.length - 3}`}
          </span>
          <Badge variant="secondary" className="text-xs">
            {group.count}
          </Badge>
        </div>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className={cn("h-full transition-all duration-300", group.color)}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Entity type pill showing count
 */
function EntityPill({ entity, count }: { entity: string; count: number }) {
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-md text-xs">
      <span className="font-medium">{entity}</span>
      <span className="text-gray-500">({count})</span>
    </div>
  );
}

/**
 * Longest running worker display (useful for detecting stuck processes)
 */
function LongestRunningWorker({ worker }: { worker: QueueWorkerInfo }) {
  const elapsedMs = Date.now() - worker.startedAt;
  const isLongRunning = elapsedMs > 2 * 60 * 1000; // > 2 minutes
  const config = getMessageTypeConfig(worker.messageType);

  return (
    <div className={cn(
      "flex items-center justify-between p-2 rounded-md border text-sm",
      isLongRunning
        ? "border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950"
        : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
    )}>
      <div className="flex items-center gap-2">
        {isLongRunning ? (
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
        ) : (
          <Clock className="h-4 w-4 text-gray-400" />
        )}
        <span className="text-gray-600 dark:text-gray-400">Longest running:</span>
        <span className="font-medium">{worker.entityType}</span>
        <span className="text-gray-500">({config.displayName})</span>
      </div>
      <span className={cn(
        "font-mono text-xs",
        isLongRunning ? "text-yellow-600 dark:text-yellow-400" : "text-gray-500"
      )}>
        {formatDistanceToNow(worker.startedAt, { addSuffix: false })}
      </span>
    </div>
  );
}

/**
 * Active Functions Panel Component
 * Shows aggregated view of running Azure Functions
 */
export function ActiveFunctionsPanel({ activeWorkers, isLoading }: ActiveFunctionsPanelProps) {
  // Aggregate workers by message type
  const messageTypeGroups = useMemo(() => {
    const groups: Record<string, MessageTypeGroup> = {};

    for (const worker of activeWorkers) {
      const type = worker.messageType || 'unknown';
      if (!groups[type]) {
        const config = getMessageTypeConfig(type);
        groups[type] = {
          type,
          displayName: config.displayName,
          count: 0,
          entities: [],
          color: config.color,
        };
      }
      groups[type].count++;
      groups[type].entities.push(worker.entityType);
    }

    // Sort by count descending
    return Object.values(groups).sort((a, b) => b.count - a.count);
  }, [activeWorkers]);

  // Aggregate workers by entity type
  const entityTypeGroups = useMemo(() => {
    const groups: Record<string, number> = {};

    for (const worker of activeWorkers) {
      const entity = worker.entityType || 'unknown';
      groups[entity] = (groups[entity] || 0) + 1;
    }

    // Sort by count descending
    return Object.entries(groups)
      .map(([entity, count]) => ({ entity, count }))
      .sort((a, b) => b.count - a.count);
  }, [activeWorkers]);

  // Find longest running worker
  const longestRunningWorker = useMemo(() => {
    if (activeWorkers.length === 0) return null;
    return activeWorkers.reduce((oldest, worker) =>
      worker.startedAt < oldest.startedAt ? worker : oldest
    );
  }, [activeWorkers]);

  const totalCount = activeWorkers.length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-lg">Active Functions</CardTitle>
            <CardDescription>
              Azure Functions currently processing
            </CardDescription>
          </div>
          <Badge
            variant={totalCount > 0 ? "default" : "secondary"}
            className={cn(
              "text-lg px-3 py-1",
              totalCount > 0 && "bg-blue-500"
            )}
          >
            {totalCount}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : totalCount === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-400">
            <div className="text-4xl mb-2">—</div>
            <div className="text-sm">No active functions</div>
          </div>
        ) : (
          <>
            {/* Message Type Breakdown */}
            <div className="space-y-3">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                By Operation Type
              </div>
              {messageTypeGroups.map((group) => (
                <MessageTypeBar key={group.type} group={group} total={totalCount} />
              ))}
            </div>

            {/* Entity Type Pills */}
            {entityTypeGroups.length > 0 && (
              <div className="space-y-2 pt-2 border-t">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  By Entity
                </div>
                <div className="flex flex-wrap gap-2">
                  {entityTypeGroups.map(({ entity, count }) => (
                    <EntityPill key={entity} entity={entity} count={count} />
                  ))}
                </div>
              </div>
            )}

            {/* Longest Running Worker */}
            {longestRunningWorker && (
              <div className="pt-2 border-t">
                <LongestRunningWorker worker={longestRunningWorker} />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
